import { memo, useEffect, useRef, useState } from 'react';

const LANDING_DOCUMENT_PATH = '/landing/index.html';
const LANDING_STYLES_PATH = '/landing/styles.0fa493023970.css';
const LANDING_SCRIPT_PATH = '/landing/script.js';
const LANDING_FONT_PATH =
  'https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap';
const API_URL = process.env.REACT_APP_API_URL || '/api';

function withoutCache(resource, version) {
  const separator = resource.includes('?') ? '&' : '?';
  return `${resource}${separator}odm_refresh=${version}`;
}

function forcePageUpdate() {
  const freshUrl = new URL(window.location.href);
  freshUrl.searchParams.set('odm_refresh', Date.now().toString());
  window.location.replace(freshUrl.toString());
}

function normalizeLandingMarkup(documentText, version) {
  const parsedDocument = new DOMParser().parseFromString(
    documentText,
    'text/html'
  );

  // React administra el runtime por separado para poder desmontarlo limpiamente.
  parsedDocument.querySelectorAll('script, noscript').forEach((node) => {
    node.remove();
  });

  parsedDocument.querySelectorAll('[src]').forEach((node) => {
    const source = node.getAttribute('src');

    if (source?.startsWith('assets/')) {
      node.setAttribute('src', withoutCache(`/landing/${source}`, version));
    }
  });

  parsedDocument.querySelectorAll('[href]').forEach((node) => {
    const href = node.getAttribute('href');

    if (href?.startsWith('assets/')) {
      node.setAttribute('href', `/landing/${href}`);
    } else if (href === 'index.html') {
      node.setAttribute('href', '/');
      node.setAttribute('data-odm-home-link', 'true');
    } else if (href?.startsWith('#') && href.length > 1) {
      node.setAttribute('data-odm-section-link', 'true');
    }
  });

  return parsedDocument.body.innerHTML;
}

function addHeadLink(attributes) {
  const link = document.createElement('link');

  Object.entries(attributes).forEach(([name, value]) => {
    link.setAttribute(name, value);
  });

  document.head.appendChild(link);
  return link;
}

/**
 * Monta la landing estática directamente en el DOM de React. Así conserva su
 * HTML y diseño, pero comparte window, navegación y acceso a las APIs de la app.
 */
function NativeLandingPage() {
  const [markup, setMarkup] = useState('');
  const [loadError, setLoadError] = useState(false);
  const refreshVersion = useRef(Date.now()).current;

  const handleLandingClick = (event) => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    const clickedElement =
      event.target instanceof Element
        ? event.target
        : event.target.parentElement;
    const homeLink = clickedElement?.closest('[data-odm-home-link]');
    const sectionLink = clickedElement?.closest('[data-odm-section-link]');

    if (homeLink && event.currentTarget.contains(homeLink)) {
      // Ya estamos en Home: evita recargar la misma ruta y destruir el runtime
      // que observa y activa las secciones conforme aparecen en pantalla.
      event.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (!sectionLink || !event.currentTarget.contains(sectionLink)) return;

    const targetId = sectionLink.getAttribute('href')?.slice(1);
    const targetSection = targetId ? document.getElementById(targetId) : null;

    if (!targetSection) return;

    // Cambiar el hash provoca que React reconstruya el HTML de la landing y
    // elimine las clases del scroll reveal. Desplazamos el documento sin
    // cambiar de ubicación para conservar el DOM y sus listeners nativos.
    event.preventDefault();
    targetSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  useEffect(() => {
    const previousTitle = document.title;
    const previousDescription = document
      .querySelector('meta[name="description"]')
      ?.getAttribute('content');
    const description = document.querySelector('meta[name="description"]');

    document.title =
      'ODM · Estudia en línea con validez oficial RVOE — Optimus Duo Mundi';
    description?.setAttribute(
      'content',
      'Universidad 100% en línea con validez oficial RVOE/SEP. Prepa, TSU, Licenciatura, Maestría y Especialidad.'
    );

    const fontLink = addHeadLink({ rel: 'stylesheet', href: LANDING_FONT_PATH });
    const styleLink = addHeadLink({
      rel: 'stylesheet',
      href: withoutCache(LANDING_STYLES_PATH, refreshVersion),
      'data-odm-landing-style': 'true',
    });

    return () => {
      document.title = previousTitle;
      if (previousDescription) {
        description?.setAttribute('content', previousDescription);
      }
      fontLink.remove();
      styleLink.remove();
    };
  }, [refreshVersion]);

  useEffect(() => {
    const refreshRestoredPage = (event) => {
      if (event.persisted) forcePageUpdate();
    };

    window.addEventListener('pageshow', refreshRestoredPage);
    return () => window.removeEventListener('pageshow', refreshRestoredPage);
  }, []);

  useEffect(() => {
    const requestController = new AbortController();

    async function loadLandingMarkup() {
      try {
        const response = await fetch(
          withoutCache(LANDING_DOCUMENT_PATH, refreshVersion),
          {
            cache: 'no-store',
            headers: {
              'Cache-Control': 'no-cache',
              Pragma: 'no-cache',
            },
            signal: requestController.signal,
          }
        );

        if (!response.ok) {
          throw new Error(`No se pudo cargar la landing (${response.status})`);
        }

        const documentText = await response.text();
        setMarkup(normalizeLandingMarkup(documentText, refreshVersion));
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error loading native landing:', error);
          setLoadError(true);
        }
      }
    }

    loadLandingMarkup();

    return () => requestController.abort();
  }, [refreshVersion]);

  useEffect(() => {
    if (!markup) return undefined;

    const year = document.querySelector('[data-odm-native-landing] #year');
    if (year) year.textContent = new Date().getFullYear();

    window.ODM_LANDING_API_URL = API_URL;
    const script = document.createElement('script');
    script.src = withoutCache(LANDING_SCRIPT_PATH, refreshVersion);
    script.dataset.odmLandingRuntime = 'true';
    document.body.appendChild(script);

    return () => {
      window.ODMlandingDestroy?.();
      delete window.ODM_LANDING_API_URL;
      script.remove();
    };
  }, [markup, refreshVersion]);

  useEffect(() => {
    if (!markup) return undefined;

    const heroVisual = document.querySelector(
      '[data-odm-native-landing] .hero-visual'
    );
    if (!heroVisual) return undefined;

    const requestController = new AbortController();
    const promotionsContainer = document.createElement('div');
    promotionsContainer.className = 'promotion-badges';
    promotionsContainer.setAttribute('aria-label', 'Promociones vigentes');
    promotionsContainer.setAttribute('aria-live', 'polite');

    const mexicoDateKey = (date) => new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/Mexico_City',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(date);

    async function loadActivePromotions() {
      try {
        const response = await fetch(`${API_URL}/promotions/active`, {
          cache: 'no-store',
          signal: requestController.signal,
        });
        if (!response.ok) throw new Error(`No se pudieron cargar las promociones (${response.status})`);

        const payload = await response.json();
        const today = mexicoDateKey(new Date());
        const activePromotions = (payload.data || []).filter((promotion) => {
          const finishDate = String(promotion.finishDate || '').slice(0, 10);
          return promotion.textLabel
            && promotion.discountLabel
            && finishDate
            && finishDate >= today;
        });

        if (requestController.signal.aborted || activePromotions.length === 0) return;

        const fragment = document.createDocumentFragment();
        activePromotions.forEach((promotion, index) => {
          const badge = document.createElement('article');
          const icon = document.createElement('span');
          const content = document.createElement('div');
          const label = document.createElement('strong');
          const discount = document.createElement('small');

          badge.className = 'promotion-badge';
          badge.style.setProperty('--promotion-delay', `${(index % 6) * 0.18}s`);
          icon.className = 'promotion-badge-icon';
          icon.setAttribute('aria-hidden', 'true');
          icon.textContent = '%';
          label.textContent = promotion.textLabel;
          discount.textContent = promotion.discountLabel;

          content.append(label, discount);
          badge.append(icon, content);
          fragment.appendChild(badge);
        });

        promotionsContainer.appendChild(fragment);
        heroVisual.appendChild(promotionsContainer);
        heroVisual.classList.add('has-promotions');
        heroVisual.classList.toggle('has-many-promotions', activePromotions.length > 4);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error loading landing promotions:', error);
        }
      }
    }

    loadActivePromotions();

    return () => {
      requestController.abort();
      promotionsContainer.remove();
      heroVisual.classList.remove('has-promotions', 'has-many-promotions');
    };
  }, [markup]);

  if (loadError) {
    return <p role="alert">No fue posible cargar la página de inicio.</p>;
  }

  return (
    <div
      data-odm-native-landing
      onClick={handleLandingClick}
      dangerouslySetInnerHTML={{ __html: markup }}
    />
  );
}

export default memo(NativeLandingPage);
